import { BaseFieldClass } from './baseField.class'
import { SectionsClass } from '../sections/sections.class'

import { IViewConfig, IWidgetField, IWidgetValue } from '@/types/serverField.type'

export class WidgetFieldClass extends BaseFieldClass {
  declare fieldValue: IWidgetValue[]
  declare initialValue: IWidgetValue[]

  _values: IWidgetValue[] = []
  _widgetRecordFields: import('@/types/serverField.type').IServerField[] | undefined
  _widget: {
    beforeGetData: Function | null
    config?: IViewConfig | null
    filterFields?: SectionsClass
  } = { beforeGetData: null }

  constructor (field: IWidgetField) {
    super(field)

    const value = field.Value ?? field.DefaultValue
    if (value) {
      this._values = this.prepareValue(value as IWidgetValue | IWidgetValue[])
      this.initialValue = JSON.parse(JSON.stringify(this._values))
      this.fieldValue = JSON.parse(JSON.stringify(this._values))
    } else {
      this.initialValue = []
      this.fieldValue = []
    }
  }

  prepareValue (value: IWidgetValue | IWidgetValue[] | null): IWidgetValue[] {
    const values = Array.isArray(value) ? value : value ? [value] : []

    return values
      .map(v => ({ ...v, Uuid: v.Uuid || v.Bundle?.Uuid }))
      .filter(v => !!v.Uuid)
  }

  // Актуальный выбор находится в _values
  override getCurrentValue (): string | string[] | null {
    if (!this._values.length) return null

    const getUuid = (value: IWidgetValue | undefined): string | undefined =>
      (value?.Uuid as string | undefined) || value?.Bundle?.Uuid
    if (this._values.length === 1) return getUuid(this._values[0]) || null

    return this._values
      .map(getUuid)
      .filter((uuid): uuid is string => Boolean(uuid))
  }

  // Помимо основного значения очищает внутренний список выбранных записей виджета.
  override clear (): void {
    super.clear()
    this._values = []
  }

  override refreshValue (_field: BaseFieldClass): void {
    if (this.name === _field.name && _field instanceof WidgetFieldClass) {
      this._values = _field._values
    }

    super.refreshValue(_field)
  }

  private buildWidgetRecordData (
    recordFields: import('@/types/serverField.type').IServerField[]
  ): [string, { Name: string; Value: string }[]][] {
    return this._values
      .map(value => {
        const uuid = value.Bundle?.Uuid ?? value.Uuid
        if (!uuid) return null

        return [
          uuid,
          recordFields.map(item => ({
            Name: item.Name,
            Value: String(item.Value ?? ''),
          })),
        ]
      })
      .filter(Boolean) as [string, { Name: string; Value: string }[]][]
  }

  getValue (): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    const recordFields = this._widgetRecordFields

    if (recordFields && Object.values(recordFields).length) {
      result[this.name] = JSON.stringify(this.buildWidgetRecordData(recordFields))
      return result
    }

    if (!this._values.length) {
      result[this.name] = null
      return result
    }

    this._values.forEach((item, index) => {
      result[`${this.name}__${index}`] = item.Bundle?.Uuid ?? item.Uuid
    })

    return result
  }

  isEmpty (): boolean {
    const skip = ['traineeEducationWidget', 'employeeEducationWidget', 'EmployeeEducationWidget', 'workplacePostTemplates']
    if (skip.includes(this.viewName)) return false
    return !this.hasValue()
  }

  appendToFormData (formData: FormData): void {
    if (this.options?.formAdapterUuid) {
      const result = this._values.map(item => [
        item.Bundle?.Uuid || item.Uuid,
        (Array.isArray(item.Fields) ? item.Fields : []).map((f: any) => {
          let _value: Record<string, unknown> | null = null
          if (Array.isArray(f.Value)) {
            _value = {}
            f.Value.forEach((v: any) => {
              if (v.Value) _value![v.Value] = v.Value
            })
          }
          return { Name: f.Name, Value: _value || f.Value || null }
        })
      ])
      formData.append(this.name, JSON.stringify(result))
    } else {
      // Если значение пустое отправляем null
      if (!this._values.length) {
        formData.append(this.name, 'null')
        return
      }

      this._values.forEach((item, index) => {
        const itemUuid = item.Bundle?.Uuid || item.Uuid
        formData.append(`${this.name}__${index}`, itemUuid as string)
      })
    }
  }

  hasValue (): boolean {
    return this._values.length > 0
  }

  getFilterValue () {
    let _values: string | string[] = ''

    if (this._values.length === 1) {
      const firstValue = this._values[0]
      _values = firstValue?.Bundle?.Uuid || firstValue?.Uuid as string
    } else if (this._values.length > 1) {
      _values = this._values.map(item => item.Bundle?.Uuid || item.Uuid as string)
    }

    return _values
  }
}
